import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import api from "@/lib/api";
import { Btn, Card, Pill, Inp, errMsg } from "@/components/datacenter/ui";

const FILTERS = ["", "ready", "warning", "incomplete", "duplicate", "review", "invalid"];
const short = (v) => (Array.isArray(v) ? v.join(", ") : v === true ? "yes" : v === false ? "no" : String(v ?? ""));

function RowDrawer({ row, job, meta, base, admin, onClose, onSaved }) {
  const fields = meta.entities[job.entity].fields;
  const mapped = job.mapping.filter((m) => m.field).map((m) => m.field);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);
  const patch = async (body, msg) => {
    setBusy(true);
    try { const r = await api.patch(`${base}/${job.id}/rows/${row.id}`, body); toast.success(msg || "Row updated"); onSaved(r.data); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const val = (k) => (k in edits ? edits[k] : row.edits?.[k] ?? short(row.values?.[k]));
  const prov = row.links?.provider;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose} data-testid="dc-row-drawer">
      <div className="h-full w-full max-w-xl bg-white overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="font-display text-lg font-semibold">Row {row.n}</div>
          <button onClick={onClose} data-testid="dc-row-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex gap-2 mb-4"><Pill s={row.status} />{row.action !== "review" && <Pill s={row.action} />}</div>
        {row.issues?.length > 0 && (
          <ul className="mb-4 space-y-1 text-xs" data-testid="dc-row-issues">
            {row.issues.map((i, k) => <li key={k} className={`rounded-lg px-2 py-1 ${i.level === "error" ? "bg-rose-50 text-rose-700" : i.level === "info" ? "bg-sky-50 text-sky-700" : "bg-amber-50 text-amber-800"}`}>{i.message}{i.suggestion ? ` — suggested: ${i.suggestion}` : ""}</li>)}
          </ul>
        )}
        {row.duplicates?.length > 0 && (
          <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50/50 p-3" data-testid="dc-row-dups">
            <div className="text-sm font-semibold mb-2">Possible existing match</div>
            {row.duplicates.map((d) => (
              <div key={d.id} className="text-sm mb-2">
                <div><b>{d.name}</b> · <Pill s={d.match === "exact" || d.match === "probable" ? "duplicate" : "warning"}>{d.match} {d.confidence}%</Pill></div>
                <div className="text-xs text-[#252A34]/60">{(d.reasons || []).join(", ")}</div>
                <div className="flex gap-1.5 mt-1.5 flex-wrap">
                  <Btn kind="ghost" className="!py-1 !px-3 !text-xs" busy={busy} onClick={() => patch({ action_override: "update", target_override: d.id }, "Will update existing")} tid={`dc-dup-update-${d.id}`}>Update existing</Btn>
                  <Btn kind="ghost" className="!py-1 !px-3 !text-xs" busy={busy} onClick={() => patch({ action_override: "merge", target_override: d.id }, "Will merge")} tid={`dc-dup-merge-${d.id}`}>Merge</Btn>
                  <Btn kind="ghost" className="!py-1 !px-3 !text-xs" busy={busy} onClick={() => patch({ action_override: "create", target_override: "" }, "Will create new")} tid={`dc-dup-create-${d.id}`}>Create new</Btn>
                  <Btn kind="ghost" className="!py-1 !px-3 !text-xs" busy={busy} onClick={() => patch({ action_override: "skip" }, "Will skip")} tid={`dc-dup-skip-${d.id}`}>Skip</Btn>
                </div>
              </div>
            ))}
          </div>
        )}
        {admin && prov?.status === "possible" && (
          <div className="mb-4 rounded-xl border border-orange-200 bg-orange-50/50 p-3" data-testid="dc-row-provider-match">
            <div className="text-sm font-semibold mb-2">Possible provider match for “{row.values?.provider_business_name}”</div>
            {prov.candidates.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm mb-1.5">
                <span>{c.name} · {c.confidence}%</span>
                <Btn className="!py-1 !px-3 !text-xs" busy={busy} onClick={() => patch({ provider_choice: { id: c.id } }, "Provider confirmed")} tid={`dc-prov-confirm-${c.id}`}>Confirm</Btn>
              </div>
            ))}
            <div className="flex gap-1.5 mt-2">
              <Btn kind="ghost" className="!py-1 !px-3 !text-xs" busy={busy} onClick={() => patch({ provider_choice: { action: "create" } }, "A new provider will be created")} tid="dc-prov-create">Create new</Btn>
              <Btn kind="ghost" className="!py-1 !px-3 !text-xs" busy={busy} onClick={() => patch({ provider_choice: { action: "unassigned" } }, "Will import unassigned")} tid="dc-prov-ignore">Ignore</Btn>
            </div>
          </div>
        )}
        {row.conflicts?.length > 0 && (
          <div className="mb-4 rounded-xl border border-indigo-200 p-3" data-testid="dc-row-conflicts">
            <div className="text-sm font-semibold mb-2">Conflicts with existing record</div>
            {row.conflicts.map((c) => (
              <div key={c.field} className="text-xs mb-2">
                <div className="font-medium">{fields.find((f) => f.key === c.field)?.label || c.field}</div>
                <div className="flex gap-1.5 mt-1">
                  {["existing", "incoming"].map((w) => (
                    <button key={w} onClick={() => patch({ conflict_choices: { [c.field]: w } })} className={`rounded-lg border px-2 py-1 text-left flex-1 ${c.choice === w ? "border-[#165a71] bg-[#165a71]/5" : "border-slate-200"}`} data-testid={`dc-conflict-${c.field}-${w}`}>
                      <div className="uppercase text-[10px] text-[#252A34]/50">{w === "existing" ? "Keep existing" : "Use import"}</div>{short(c[w])}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="text-sm font-semibold mb-2">Edit values before import</div>
        <div className="space-y-2">
          {mapped.map((k) => (
            <label key={k} className="block">
              <div className="text-xs text-[#252A34]/60">{fields.find((f) => f.key === k)?.label || k}{row.raw && <span className="text-[#252A34]/40"> · original: {short(row.raw[job.mapping.find((m) => m.field === k)?.column]) || "—"}</span>}</div>
              <Inp value={val(k)} onChange={(e) => setEdits((x) => ({ ...x, [k]: e.target.value }))} className="w-full" data-testid={`dc-edit-${k}`} />
            </label>
          ))}
        </div>
        <div className="mt-5 flex gap-2 justify-between">
          {row.action_override ? <Btn kind="ghost" busy={busy} onClick={() => patch({ clear_override: true }, "Reset to automatic")} tid="dc-row-reset">Reset decision</Btn> : <span />}
          <Btn busy={busy} disabled={!Object.keys(edits).length} onClick={() => patch({ edits }, "Row re-validated")} tid="dc-row-save">Save changes</Btn>
        </div>
      </div>
    </div>
  );
}

export default function PreviewStep({ job, meta, base, admin, refreshKey, onChanged }) {
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get(`${base}/${job.id}/rows`, { params: { status: status || undefined, page, size: 50 } }).then((r) => setData(r.data));
  useEffect(() => { load(); }, [status, page, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const cols = job.mapping.filter((m) => m.field).slice(0, 5);
  const s = job.summary || {};
  return (
    <Card tid="dc-preview-step">
      <div className="font-display text-lg font-semibold text-[#071b22] mb-3">Preview records</div>
      <div className="flex gap-1.5 flex-wrap mb-4" data-testid="dc-preview-filters">
        {FILTERS.map((f) => (
          <button key={f || "all"} onClick={() => { setStatus(f); setPage(1); }} className={`rounded-full border px-3 py-1 text-xs capitalize ${status === f ? "bg-[#071b22] text-white border-[#071b22]" : "border-slate-200"}`} data-testid={`dc-filter-${f || "all"}`}>
            {f || "all"} {f ? `(${s[f] || 0})` : `(${s.total || 0})`}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="dc-preview-table">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-[#252A34]/50"><th className="py-2 pr-2">Row</th><th className="pr-2">Status</th><th className="pr-2">Action</th>{cols.map((c) => <th key={c.column} className="pr-2">{meta.entities[job.entity].fields.find((f) => f.key === c.field)?.label}</th>)}<th>Notes</th></tr></thead>
          <tbody>
            {(data?.items || []).map((r) => (
              <tr key={r.id} onClick={() => setOpen(r)} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" data-testid={`dc-preview-row-${r.n}`}>
                <td className="py-2 pr-2 text-xs text-[#252A34]/50">{r.n}</td>
                <td className="pr-2"><Pill s={r.status} tid={`dc-row-status-${r.n}`} /></td>
                <td className="pr-2"><Pill s={r.result?.action || r.action} tid={`dc-row-action-${r.n}`} /></td>
                {cols.map((c) => <td key={c.column} className="pr-2 max-w-[180px] truncate">{short(r.values?.[c.field]) || <span className="text-[#252A34]/30">—</span>}</td>)}
                <td className="text-xs text-[#252A34]/60 max-w-[260px] truncate">{r.result?.error || (r.issues || []).filter((i) => i.level !== "info" || !r.issues.some((x) => x.level !== "info")).map((i) => i.message).slice(0, 2).join(" · ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data && data.total > data.size && (
        <div className="mt-3 flex items-center gap-2 text-sm">
          <Btn kind="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)} tid="dc-prev-page">Previous</Btn>
          <span>Page {page} of {Math.ceil(data.total / data.size)}</span>
          <Btn kind="ghost" disabled={page * data.size >= data.total} onClick={() => setPage(page + 1)} tid="dc-next-page">Next</Btn>
        </div>
      )}
      {open && job.stage === "validated" && <RowDrawer row={open} job={job} meta={meta} base={base} admin={admin} onClose={() => setOpen(null)} onSaved={(r) => { setOpen(r); load(); onChanged(); }} />}
    </Card>
  );
}
